import { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import { MdRemove, MdAdd, MdDelete, MdShoppingCart, MdArrowBack } from 'react-icons/md';


function Cart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingItemId, setUpdatingItemId] = useState(null);

  // Extract fetch logic into a reusable function
  const fetchCartItems = async (userId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      logger.log(`📦 Fetching cart items for user ${userId}`);
      const response = await api.get(`/aastrog_cart/cart/items?userId=${userId}`);
      
      if (response.data.success) {
        setCartItems(response.data.data || []);
        logger.log(`✅ Cart fetched: ${response.data.data?.length || 0} items`);
      } else {
        throw new Error('Failed to fetch cart items');
      }
    } catch (err) {
      logger.error('❌ Error fetching cart:', err);
      setError(err.response?.data?.error || 'Failed to load cart. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch cart items on component mount and when user changes
  useEffect(() => {
    const userId = user?.userId || 1;
    logger.log(`📦 Cart useEffect triggered - userId: ${userId}`);
    fetchCartItems(userId);
  }, [user?.userId]);

  // Update quantity
  const updateQuantity = async (userCartDetailId, newQuantity) => {
    // Find the item to check stock
    const item = cartItems.find(i => i.UserCartDetailId === userCartDetailId);
    
    if (!item) {
      setError('Item not found in cart');
      return;
    }

    // Check if quantity exceeds available stock
    if (newQuantity > item.StockQty) {
      setError(`⚠️ Only ${item.StockQty} items in stock for ${item.ProductName}`);
      setUpdatingItemId(null);
      return;
    }

    if (newQuantity < 1) {
      await removeFromCart(userCartDetailId);
      return;
    }

    try {
      setUpdatingItemId(userCartDetailId);
      logger.log(`🔄 Updating quantity for item ${userCartDetailId} to ${newQuantity} (Stock: ${item.StockQty})`);

      const response = await api.put('/aastrog_cart/cart/update-quantity', {
        userCartDetailId,
        quantity: newQuantity
      });

      if (response.data.success) {
        // Update local state
        setCartItems(prevItems =>
          prevItems.map(itemData =>
            itemData.UserCartDetailId === userCartDetailId
              ? { ...itemData, Quantity: newQuantity }
              : itemData
          )
        );
        setError(null);
        logger.log(`✅ Quantity updated successfully`);
      }
    } catch (err) {
      logger.error('❌ Error updating quantity:', err);
      setError('Failed to update quantity');
    } finally {
      setUpdatingItemId(null);
    }
  };

  // Remove from cart
  const removeFromCart = async (userCartDetailId) => {
    try {
      setUpdatingItemId(userCartDetailId);
      logger.log(`🗑️ Removing item ${userCartDetailId} from cart`);

      const response = await api.delete(`/aastrog_cart/cart/remove/${userCartDetailId}`);
      logger.log('Delete response:', response.data);

      if (response.data && response.data.success) {
        // Immediately remove from UI
        setCartItems(prevItems => {
          const updated = prevItems.filter(item => item.UserCartDetailId !== userCartDetailId);
          logger.log(`✅ Item removed from cart. Remaining items:`, updated.length);
          return updated;
        });
        setError(null);
      } else {
        logger.error('❌ Delete response not successful:', response.data);
        setError('Failed to remove item');
        // Refresh cart items to sync with backend
        await fetchCartItems();
      }
    } catch (err) {
      logger.error('❌ Error removing item:', err);
      setError('Failed to remove item: ' + (err.response?.data?.error || err.message));
      // Refresh cart items to sync with backend
      await fetchCartItems();
    } finally {
      setUpdatingItemId(null);
    }
  };

  // Proceed to checkout - require login and then navigate to checkout page
  const handleProceedToCheckout = () => {
    // If not authenticated, send user to login (root '/'), ProtectedRoute also guards /checkout
    if (!user || !user.userId) {
      logger.log('➡️ User not logged in - redirecting to login');
      navigate('/');
      return;
    }

    logger.log('➡️ Navigating to /checkout');
    navigate('/checkout');
  };

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + (item.Price * item.Quantity), 0);
  const tax = subtotal * 0.18; // 18% GST
  const total = subtotal + tax;

  if (isLoading) {
    return (
      <div className="cart-container">
        <div className="loading-message">
          <span>⏳ Loading your cart...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container shopping-cart-page">
      {/* Header */}
      <div className="cart-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <MdArrowBack size={24} /> Back
        </button>
        <h1 className="cart-title">
          <MdShoppingCart size={26} /> Shopping Cart 
        </h1>
        <div className="cart-count">
          {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}

      {/* Empty Cart */}
      {cartItems.length === 0 ? (
        <div className="empty-cart">
          <MdShoppingCart size={64} className="empty-cart-icon" />
          <h2>Your cart is empty</h2>
          <p>Start shopping to add items to your cart</p>
          <button
            className="continue-shopping-btn"
            onClick={() => navigate('/products')}
          >
            Continue Shopping
          </button>
        </div>
      ) : (
        <div className="cart-content">
          {/* Cart Items */}
          <div className="cart-items-section">
            <h2 className="section-title">Your Items</h2>

            <div className="cart-items-list">
              {cartItems.map((item) => (
                <div key={item.UserCartDetailId} className="cart-item">
                  {/* Product Image */}
                  <div className="item-image">
                    <img
                      src={item.ImageUrl || 'https://via.placeholder.com/100'}
                      alt={item.ProductName}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100';
                      }}
                    />
                  </div>

                  {/* Product Details */}
                  <div className="item-details">
                    <h3 className="item-name">{item.ProductName}</h3>
                    <p className="item-category">{item.CategoryName}</p>
                    <p className="item-price">₹{Number(item.Price).toFixed(2)}</p>
                    <p className="item-stock" style={{
                      color: item.StockQty > 5 ? '#4CAF50' : item.StockQty > 0 ? '#ff9800' : '#f44336',
                      fontSize: '12px',
                      marginTop: '4px'
                    }}>
                      {item.StockQty > 0 ? `📦 ${item.StockQty} in stock` : '❌ Out of Stock'}
                    </p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="quantity-control">
                    <button
                      className="qty-btn minus-btn"
                      onClick={() => updateQuantity(item.UserCartDetailId, item.Quantity - 1)}
                      disabled={updatingItemId === item.UserCartDetailId || item.Quantity <= 1}
                      title="Decrease quantity"
                    >
                      <MdRemove size={20} />
                    </button>

                    <input
                      type="number"
                      className="qty-input"
                      value={item.Quantity}
                      onChange={(e) => {
                        const qty = parseInt(e.target.value) || 1;
                        if (qty > item.StockQty) {
                          setError(`⚠️ Only ${item.StockQty} items in stock for ${item.ProductName}`);
                        } else if (qty > 0) {
                          updateQuantity(item.UserCartDetailId, qty);
                        }
                      }}
                      disabled={updatingItemId === item.UserCartDetailId}
                      min="1"
                      max={item.StockQty}
                    />

                    <button
                      className="qty-btn plus-btn"
                      onClick={() => updateQuantity(item.UserCartDetailId, item.Quantity + 1)}
                      disabled={updatingItemId === item.UserCartDetailId || item.Quantity >= item.StockQty}
                      title={item.Quantity >= item.StockQty ? "Out of Stock" : "Increase quantity"}
                    >
                      <MdAdd size={20} />
                    </button>
                  </div>

                  {/* Item Total */}
                  <div className="item-total">
                    <div className="total-amount">
                      ₹{((item.Price || 0) * item.Quantity).toFixed(2)}
                    </div>
                    <div className="total-label">Total</div>
                  </div>

                  {/* Remove Button */}
                  <button
                    className="remove-btn"
                    onClick={() => removeFromCart(item.UserCartDetailId)}
                    disabled={updatingItemId === item.UserCartDetailId}
                    title="Remove item"
                  >
                    <MdDelete size={20} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="order-summary">
            <h2 className="summary-title">Order Summary</h2>

            <div className="summary-row">
              <span className="summary-label">Subtotal</span>
              <span className="summary-value">₹{subtotal.toFixed(2)}</span>
            </div>

            <div className="summary-row">
              <span className="summary-label">Tax (18% GST)</span>
              <span className="summary-value">₹{tax.toFixed(2)}</span>
            </div>

            <div className="summary-row shipping">
              <span className="summary-label">Shipping</span>
              <span className="summary-value free">FREE</span>
            </div>

            <div className="summary-divider"></div>

            <div className="summary-row total">
              <span className="summary-label">Total Amount</span>
              <span className="summary-value">₹{total.toFixed(2)}</span>
            </div>

            {/* Checkout Button */}
            <button className="checkout-btn" onClick={handleProceedToCheckout}>
              Proceed to Checkout
            </button>

            {/* Continue Shopping Button */}
            <button
              className="continue-btn"
              onClick={() => navigate('/products')}
            >
              Continue Shopping
            </button>

            {/* Security Info */}
            <div className="security-info">
              <p>🔒 Secure checkout with encrypted payment</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;
